/**
 * Subscriber Verification Console (Wave 2)
 *
 * Founder-only page that lets the founder:
 *   1. Run the 8-oracle `verifyOnboardingReady` harness against any subscriber.
 *   2. View live dashboard stats (Rolling 3-Run, LOT growth, Monthly
 *      Commissions, Loan Portfolio Stats) for that subscriber.
 *
 * Hard-gated by `AdminRouteGuard` (founder + MFA) at the route layer; soft-
 * gated by `useFounderViewKillSwitch` at render layer (emergency lever).
 *
 * The "E2E" tab is read-only: the onboarding-journey workflow and its
 * dispatch callable were retired on 2026-09-13 by founder decision.
 *
 * @see Plan: i-need-a-way-giggly-babbage.md (Wave 2 — Console UI)
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  ChevronLeft,
  PlayCircle,
  Loader,
  RefreshCw,
  ShieldCheck,
  XCircle,
  CheckCircle,
  AlertTriangle,
  Lock,
  HandCoins,
  TrendingUp,
  Activity,
  Vault,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { db, functions } from '../firebaseConfig';
import { useAdminAuditLog } from '../hooks/useAdminAuditLog';
import { useOnboardingReadiness } from '../hooks/useOnboardingReadiness';
import { useFounderViewKillSwitch } from '../hooks/useFounderViewKillSwitch';
import { useDashboardCallable } from '../hooks/useDashboardCallable';
import { OracleResultsTable } from '../components/OracleResultsTable';
import { SubscriberVerificationBanner } from '../components/SubscriberVerificationBanner';

// ─── Types ───────────────────────────────────────────────────────────────────

type TabId = 'readiness' | 'live-stats' | 'e2e';

interface CompanyDoc {
  id: string;
  companyName?: string;
  name?: string;
  status?: string;
  /**
   * Receipt written by `scripts/onboarding/manual-onboard-company.ts` stage 13
   * (PR #575) when the 8-oracle readiness harness goes all-green. Used by the
   * "View as Customer" launcher (PR-C) to gate cross-tenant view-as on a
   * recent (≤24h) all-green run, so operators are forced to refresh readiness
   * before viewing.
   */
  onboardingReadinessReceipt?: {
    allGreenAt?: { seconds: number; nanoseconds: number } | null;
    allGreenAtIso?: string;
    blockingFailures?: number;
    warnings?: number;
    harnessVersion?: string;
    runBy?: string;
  };
}

// ─── Founder view-as launcher constants ───────────────────────────────────────

/**
 * Per-environment client-app URL for the founder view-as launcher (PR-C).
 * Stage uses `.web.app` because the `app-stage.valutten.com` custom-domain
 * CNAME is misdirected to the Loyalty app (verified 2026-05-07; see
 * memory/reference_valutten_hosting_urls.md).
 */
const CLIENT_APP_URLS_BY_PROJECT: Record<string, string> = {
  'valutten-prod': 'https://app.valutten.com',
  'valutten-stage': 'https://valutten-stage.web.app',
  'valutten-dev': 'https://valutten-dev.web.app',
};

const CLIENT_APP_URL =
  CLIENT_APP_URLS_BY_PROJECT[process.env.REACT_APP_PROJECT_ID || 'valutten-prod'] ||
  'https://app.valutten.com';

/**
 * Maximum staleness of `onboardingReadinessReceipt.allGreenAt` allowed before
 * the launcher disables itself. 24h matches the operator policy: re-run the
 * readiness harness daily before using view-as on any subscriber.
 */
const VIEW_AS_RECEIPT_FRESHNESS_MS = 24 * 60 * 60 * 1000;

// Loose shape of dashboard responses we care about — we only render specific
// numeric fields. Other fields are ignored.
interface Rolling3RunResponse {
  totalLineOfTrail?: number;
  totalLoanBook?: number;
  totalUpfrontNet?: number;
  totalTrailNet?: number;
  totalRecords?: number;
}

interface LineOfTrailGrowthResponse {
  // Server returns an array of monthly points or aggregate; we render whichever
  // top-level numeric we can find. Use `unknown` for safety.
  current?: number;
  total?: number;
  growth?: number;
  months?: Array<{ month: string; value?: number; lot?: number }>;
}

interface MonthlyCommissionsResponse {
  totals?: {
    upfront?: number;
    trail?: number;
    total?: number;
  };
  rows?: unknown[];
}

interface LoanPortfolioStatsResponse {
  activeLoans?: number;
  totalLoans?: number;
  totalLoanBookCents?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Format integer cents as AUD dollars. Returns "—" when value is null/undef.
 */
const formatCents = (cents: number | undefined | null): string => {
  if (cents === null || cents === undefined || Number.isNaN(cents)) return '—';
  const dollars = cents / 100;
  return dollars.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Format a plain integer count. Returns "—" on missing.
 */
const formatCount = (n: number | undefined | null): string => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString('en-AU');
};

const formatRelativeTime = (date: Date | null): string => {
  if (!date) return 'never';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return date.toLocaleTimeString('en-AU');
};

// ─── Stat Card ───────────────────────────────────────────────────────────────

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  lastFetchedAt: Date | null;
  iconColorClass?: string;
}

const StatCard: React.FC<StatCardProps> = ({
  icon: Icon,
  label,
  value,
  loading,
  error,
  onRefresh,
  lastFetchedAt,
  iconColorClass = 'text-blue-400',
}) => {
  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all">
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-6 h-6 ${iconColorClass}`} />
        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-1.5 rounded-lg hover:bg-slate-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`Refresh ${label}`}
        >
          <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-sm text-slate-400 uppercase tracking-wide">{label}</p>
      {error ? (
        <p className="text-sm text-red-400 mt-2 break-words" title={error}>
          Error: {error.length > 80 ? `${error.slice(0, 80)}…` : error}
        </p>
      ) : loading && !value ? (
        <p className="text-2xl font-bold text-slate-500 mt-2">Loading…</p>
      ) : (
        <p className="text-2xl font-bold text-white mt-2 break-words">{value}</p>
      )}
      <p className="text-xs text-slate-500 mt-2">
        Last refreshed: {formatRelativeTime(lastFetchedAt)}
      </p>
    </div>
  );
};

// ─── Tab Button ──────────────────────────────────────────────────────────────

interface TabButtonProps {
  id: TabId;
  label: string;
  active: boolean;
  disabled?: boolean;
  tooltip?: string;
  onClick: (id: TabId) => void;
}

const TabButton: React.FC<TabButtonProps> = ({
  id,
  label,
  active,
  disabled,
  tooltip,
  onClick,
}) => {
  return (
    <button
      type="button"
      disabled={disabled}
      title={tooltip}
      onClick={() => !disabled && onClick(id)}
      className={`px-6 py-3 rounded-lg font-medium transition-all ${
        disabled
          ? 'text-slate-600 cursor-not-allowed'
          : active
            ? 'bg-blue-500/20 text-white shadow-lg border border-blue-500/30'
            : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
      }`}
    >
      {label}
      {disabled && <span className="ml-2 text-xs">(soon)</span>}
    </button>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const SubscriberVerificationConsolePage: React.FC = () => {
  const { companyId = '' } = useParams<{ companyId: string }>();
  const { logAction } = useAdminAuditLog();
  const killSwitch = useFounderViewKillSwitch();
  const readiness = useOnboardingReadiness();

  const [activeTab, setActiveTab] = useState<TabId>('readiness');
  const [company, setCompany] = useState<CompanyDoc | null>(null);
  const [companyLoading, setCompanyLoading] = useState<boolean>(true);
  const [companyError, setCompanyError] = useState<string | null>(null);

  // Slate background — match the rest of the admin app.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'companies');
    return () => {
      document.documentElement.removeAttribute('data-theme');
    };
  }, []);

  // Load minimal company doc for header display.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    setCompanyLoading(true);
    setCompanyError(null);

    (async () => {
      try {
        const ref = doc(db, 'companies', companyId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (!snap.exists()) {
          setCompanyError(`Company ${companyId} not found`);
          setCompany(null);
        } else {
          setCompany({ id: snap.id, ...(snap.data() as Omit<CompanyDoc, 'id'>) });
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load company';
        console.error('[SubscriberVerificationConsole] load company error:', err);
        setCompanyError(msg);
      } finally {
        if (!cancelled) setCompanyLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Audit log: founder cross-tenant view (Privacy Act APP 13).
  useEffect(() => {
    if (!companyId || companyLoading) return;
    void logAction({
      action: 'ADMIN_SUBSCRIBER_VERIFICATION_VIEWED',
      resource: `companies/${companyId}`,
      companyId,
      companyName: company?.companyName || company?.name || undefined,
      resourceName: company?.companyName || company?.name || companyId,
      metadata: { tab: activeTab },
    });
  }, [companyId, companyLoading, company, activeTab, logAction]);

  // Live Stats callables — fired in parallel only when the tab is active for
  // the first time. We use `manual: true` here and trigger via the inner
  // `LiveStatsTab` so we don't burn server quota for users who never click it.
  const displayName = useMemo(
    () => company?.companyName || company?.name || companyId || 'Subscriber',
    [company, companyId]
  );

  // ─── Founder view-as launcher (PR-C) ──────────────────────────────────────
  // Disabled unless the company has an `onboardingReadinessReceipt` whose
  // `allGreenAt` is within the last 24h. The receipt is written by stage 13
  // of `scripts/onboarding/manual-onboard-company.ts` when the 8-oracle harness
  // goes all-green. Forcing a recent green run before view-as means operators
  // can't accidentally view a tenant whose data is in a half-broken state.
  //
  // WIDER SINCE 2026-09-14 (PRs #2599 + #2606), stated because it changes when
  // this button is available: `login.mfa_readiness` is now CRITICAL for an
  // enforcing tenant whose primary admin holds no native factor and no live
  // recovery exemption, and for a malformed stored enforcement mode. Neither
  // writer stamps a receipt while a CRITICAL stands, so view-as stays disabled
  // for such a tenant until the lockout is cleared. That is the intended
  // direction: view-as on a tenant whose admin cannot sign in shows the founder
  // a working session the customer does not have.
  const viewAsGate = useMemo(() => {
    const receipt = company?.onboardingReadinessReceipt;
    if (!receipt) {
      return { available: false as const, reason: 'no_receipt', hoursAgo: null as number | null };
    }
    const allGreenAtMs =
      receipt.allGreenAt?.seconds !== undefined
        ? receipt.allGreenAt.seconds * 1000
        : receipt.allGreenAtIso
          ? new Date(receipt.allGreenAtIso).getTime()
          : NaN;
    if (!Number.isFinite(allGreenAtMs)) {
      return { available: false as const, reason: 'invalid_timestamp', hoursAgo: null };
    }
    const ageMs = Date.now() - allGreenAtMs;
    const hoursAgo = ageMs / (60 * 60 * 1000);
    if (ageMs > VIEW_AS_RECEIPT_FRESHNESS_MS) {
      return { available: false as const, reason: 'stale', hoursAgo };
    }
    if ((receipt.blockingFailures ?? 0) > 0) {
      return { available: false as const, reason: 'blocking_failures', hoursAgo };
    }
    return { available: true as const, reason: 'fresh', hoursAgo };
  }, [company?.onboardingReadinessReceipt]);

  const [viewAsLaunching, setViewAsLaunching] = useState(false);

  const handleLaunchViewAs = useCallback(async () => {
    if (!viewAsGate.available || viewAsLaunching) return;
    setViewAsLaunching(true);
    try {
      // Audit FIRST, synchronously: if the audit log fails, do NOT open the
      // tab. This is the rule for cross-tenant founder reads — the founder's
      // session must be discoverable in `admin_audit_logs` before any data
      // is rendered. Same pattern as the Console's existing
      // `ADMIN_SUBSCRIBER_VERIFICATION_VIEWED` emit.
      await logAction({
        action: 'ADMIN_COMPANY_VIEWED',
        resource: `companies/${companyId}`,
        companyId,
        companyName: company?.companyName || company?.name || undefined,
        resourceName: company?.companyName || company?.name || companyId,
        metadata: {
          mode: 'view-as',
          launcher: 'admin-console',
          clientAppUrl: CLIENT_APP_URL,
          receiptAllGreenAtIso: company?.onboardingReadinessReceipt?.allGreenAtIso ?? null,
          receiptHoursAgo: viewAsGate.hoursAgo,
        },
      });
      const url = `${CLIENT_APP_URL}/?viewAs=${encodeURIComponent(companyId)}`;
      // _blank + noopener,noreferrer: the new tab cannot script back into this
      // admin tab (window.opener === null), so a malicious client-app load
      // can't reach the admin session.
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('[SubscriberVerificationConsole] view-as launch audit failed:', err);
      // Do NOT open the tab if audit failed — surfaced to operator via the
      // launching state staying briefly on, then the toast/error UI below.
    } finally {
      setViewAsLaunching(false);
    }
  }, [viewAsGate.available, viewAsLaunching, companyId, company, logAction, viewAsGate.hoursAgo]);

  // ─── Kill switch UI ────────────────────────────────────────────────
  if (killSwitch.loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-slate-300">
        <Loader className="w-6 h-6 animate-spin mr-2" />
        Loading feature flag…
      </div>
    );
  }

  if (!killSwitch.enabled) {
    return (
      <div className="p-8">
        <div className="max-w-2xl mx-auto bg-red-500/10 border border-red-500/30 rounded-xl p-8 text-center">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Feature disabled</h2>
          <p className="text-slate-300 mb-4">
            The Subscriber Verification Console has been disabled via{' '}
            <code className="font-mono text-amber-300">
              admin_config/featureFlags.subscriberVerificationConsoleEnabled
            </code>
            .
          </p>
          <p className="text-slate-400 text-sm">
            Flip the flag back to <code className="font-mono">true</code> (or remove the field) to
            re-enable.
          </p>
          <Link
            to="/companies"
            className="inline-flex items-center gap-2 mt-6 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="p-8 text-red-400">Missing companyId in URL.</div>
    );
  }

  return (
    <div className="p-8">
      <SubscriberVerificationBanner
        companyName={displayName}
        companyId={companyId}
      />

      {/* Page header */}
      <div className="rounded-2xl p-8 mb-6 shadow-2xl relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-4 -right-4 w-64 h-64 bg-white/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-4 -left-4 w-48 h-48 bg-white/20 rounded-full blur-2xl animate-pulse delay-700" />
        </div>
        <div className="relative">
          <Link
            to={`/companies/${companyId}`}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-4"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to {displayName}
          </Link>
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                <ShieldCheck className="w-10 h-10 text-white flex-shrink-0" />
                <h1 className="text-4xl font-bold text-white">Subscriber Verification Console</h1>
              </div>
              <p className="text-white/90 text-lg">
                Run readiness oracles and inspect live dashboards for{' '}
                <span className="font-semibold">{displayName}</span>.
              </p>
            </div>
            {/* View-as Customer launcher — gated on fresh readiness receipt. */}
            <div className="flex-shrink-0">
              <button
                type="button"
                onClick={handleLaunchViewAs}
                disabled={!viewAsGate.available || viewAsLaunching}
                title={
                  viewAsGate.available
                    ? `Open ${displayName} dashboard via founder view-as. Audited; read-only; 15-min auto-expiry. Receipt: ${viewAsGate.hoursAgo!.toFixed(1)}h ago.`
                    : viewAsGate.reason === 'no_receipt'
                      ? 'No readiness receipt found. Run the readiness harness (Readiness tab below) to enable view-as.'
                      : viewAsGate.reason === 'stale'
                        ? `Readiness receipt is ${viewAsGate.hoursAgo!.toFixed(1)}h old (> 24h). Re-run the harness to enable view-as.`
                        : viewAsGate.reason === 'blocking_failures'
                          ? 'Readiness receipt has CRITICAL failures. Resolve and re-run before view-as.'
                          : 'Readiness receipt invalid. Re-run the harness.'
                }
                className={[
                  'inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold shadow-lg transition-all',
                  'bg-white text-purple-700 hover:bg-purple-50 disabled:bg-white/30 disabled:text-white/60',
                  'disabled:cursor-not-allowed disabled:shadow-none',
                  'focus:outline-none focus:ring-4 focus:ring-white/30',
                ].join(' ')}
                aria-label={`View ${displayName} dashboard as customer`}
              >
                {viewAsLaunching ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
                <span>View as Customer</span>
                {viewAsGate.available && !viewAsLaunching && (
                  <ExternalLink className="w-4 h-4 opacity-70" />
                )}
              </button>
              {/* Receipt freshness footnote */}
              <p className="mt-2 text-xs text-white/70 text-right max-w-[14rem]">
                {viewAsGate.available
                  ? `Receipt fresh — ${viewAsGate.hoursAgo!.toFixed(1)}h ago`
                  : viewAsGate.reason === 'no_receipt'
                    ? 'Run readiness harness first'
                    : viewAsGate.reason === 'stale'
                      ? `Receipt stale (${viewAsGate.hoursAgo!.toFixed(1)}h old)`
                      : viewAsGate.reason === 'blocking_failures'
                        ? 'Receipt has failures'
                        : 'Receipt invalid'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {companyError && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 backdrop-blur-sm">
          {companyError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
        <TabButton
          id="readiness"
          label="Readiness"
          active={activeTab === 'readiness'}
          onClick={setActiveTab}
        />
        <TabButton
          id="live-stats"
          label="Live Stats"
          active={activeTab === 'live-stats'}
          onClick={setActiveTab}
        />
        <TabButton
          id="e2e"
          label="E2E"
          active={activeTab === 'e2e'}
          onClick={setActiveTab}
        />
      </div>

      {/* Tab content */}
      {activeTab === 'readiness' && (
        <ReadinessTab companyId={companyId} readiness={readiness} />
      )}
      {activeTab === 'live-stats' && (
        <LiveStatsTab companyId={companyId} />
      )}
      {activeTab === 'e2e' && (
        <E2ETab companyId={companyId} />
      )}
    </div>
  );
};

// ─── Readiness Tab ───────────────────────────────────────────────────────────

interface ReadinessTabProps {
  companyId: string;
  readiness: ReturnType<typeof useOnboardingReadiness>;
}

const ReadinessTab: React.FC<ReadinessTabProps> = ({ companyId, readiness }) => {
  const handleRun = () => {
    void readiness.run(companyId);
  };

  return (
    <div className="space-y-6">
      {/* Run control */}
      <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-white mb-1">Onboarding Readiness Harness</h2>
            <p className="text-sm text-slate-400">
              Runs 8 oracles (4 login + 4 dashboard) against this subscriber.
              CRITICAL failures block <code className="font-mono text-amber-300">approveOnboardingActivation</code>.
            </p>
          </div>
          <button
            onClick={handleRun}
            disabled={readiness.loading}
            className="px-5 py-3 bg-blue-500/20 text-white rounded-lg border border-blue-500/30 hover:bg-blue-500/30 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
          >
            {readiness.loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Running…
              </>
            ) : (
              <>
                <PlayCircle className="w-5 h-5" />
                Run Verification
              </>
            )}
          </button>
        </div>

        {readiness.error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            <strong>Verification call failed:</strong> {readiness.error}
          </div>
        )}
      </div>

      {/* Result summary */}
      {readiness.result && (
        <ReadinessSummary result={readiness.result} />
      )}

      {/* Per-oracle table */}
      {readiness.result && (
        <OracleResultsTable
          loginOracles={readiness.result.loginReadiness.oracles}
          dashboardOracles={readiness.result.dashboardReadiness.oracles}
        />
      )}

      {!readiness.result && !readiness.loading && !readiness.error && (
        <div className="p-12 text-center bg-slate-800/20 backdrop-blur-sm rounded-xl border border-slate-700/30">
          <ShieldCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">
            Click <strong className="text-white">Run Verification</strong> to execute the readiness harness.
          </p>
        </div>
      )}
    </div>
  );
};

const ReadinessSummary: React.FC<{ result: NonNullable<ReturnType<typeof useOnboardingReadiness>['result']> }> = ({
  result,
}) => {
  const passed = result.passed;
  return (
    <div
      className={`rounded-xl p-6 border ${
        passed
          ? 'bg-green-500/10 border-green-500/30'
          : 'bg-red-500/10 border-red-500/30'
      }`}
    >
      <div className="flex items-start gap-4">
        {passed ? (
          <CheckCircle className="w-10 h-10 text-green-400 flex-shrink-0" />
        ) : (
          <XCircle className="w-10 h-10 text-red-400 flex-shrink-0" />
        )}
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-white">
            {passed ? 'Subscriber is ready' : 'Subscriber is NOT ready'}
          </h3>
          <p className="text-slate-300 mt-1">{result.diagnostic}</p>
          <div className="flex gap-4 mt-3 text-sm flex-wrap">
            <span className="text-slate-400">
              Verified at:{' '}
              <span className="font-mono text-slate-200">
                {new Date(result.verifiedAt).toLocaleString('en-AU')}
              </span>
            </span>
            <span className="text-red-300">
              Critical failures: <strong>{result.blockingFailures}</strong>
            </span>
            <span className="text-yellow-300 inline-flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Warnings: <strong>{result.warnings}</strong>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Live Stats Tab ──────────────────────────────────────────────────────────

interface LiveStatsTabProps {
  companyId: string;
}

/**
 * Format a Date as `YYYY-MM` (UTC). Mirrors the helper of the same name in
 * `services/admin-functions/src/verification/onboardingReadinessOracles.ts`
 * so the Console window matches the harness window exactly.
 */
const formatYearMonth = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Subtract whole calendar months (UTC). Same shape as the harness helper.
 */
const subtractCalendarMonths = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  result.setUTCMonth(result.getUTCMonth() - months);
  return result;
};

const LiveStatsTab: React.FC<LiveStatsTabProps> = ({ companyId }) => {
  // Period args. Computed once on mount — these dashboards' periodicity is
  // "right now relative to page open"; we don't need them to drift mid-session.
  const periodArgs = useMemo(() => {
    const now = new Date();
    const startMonth = formatYearMonth(subtractCalendarMonths(now, 12));
    const endMonth = formatYearMonth(now);
    const year = now.getUTCFullYear();
    return { startMonth, endMonth, year };
  }, []);

  const rolling3Payload = useMemo<Record<string, unknown>>(
    () => ({ startMonth: periodArgs.startMonth, endMonth: periodArgs.endMonth }),
    [periodArgs.startMonth, periodArgs.endMonth]
  );
  const lotGrowthPayload = useMemo<Record<string, unknown>>(
    () => ({ startMonth: periodArgs.startMonth, endMonth: periodArgs.endMonth }),
    [periodArgs.startMonth, periodArgs.endMonth]
  );
  const monthlyCommPayload = useMemo<Record<string, unknown>>(
    () => ({ year: periodArgs.year }),
    [periodArgs.year]
  );

  // Fire all 4 callables in parallel on mount.
  // - Rolling 3-Run + LOT Growth: rolling 12-month window (matches harness oracles 5/6).
  // - Monthly Commissions: current calendar year.
  // - Loan Portfolio Stats: companyId only (no period arg required).
  const rolling3 = useDashboardCallable<Rolling3RunResponse>(
    'getRolling3Run',
    companyId,
    { extraPayload: rolling3Payload }
  );
  const lotGrowth = useDashboardCallable<LineOfTrailGrowthResponse>(
    'getLineOfTrailGrowth',
    companyId,
    { extraPayload: lotGrowthPayload }
  );
  const monthlyComm = useDashboardCallable<MonthlyCommissionsResponse>(
    'getMonthlyCommissions',
    companyId,
    { extraPayload: monthlyCommPayload }
  );
  const portfolio = useDashboardCallable<LoanPortfolioStatsResponse>(
    'getLoanPortfolioStats',
    companyId
  );

  // Derive display values defensively — server response shapes may vary.
  const rolling3Value = formatCents(rolling3.data?.totalLineOfTrail);

  const lotGrowthRaw =
    lotGrowth.data?.current ??
    lotGrowth.data?.total ??
    (lotGrowth.data?.months && lotGrowth.data.months.length > 0
      ? (lotGrowth.data.months[lotGrowth.data.months.length - 1].lot ??
         lotGrowth.data.months[lotGrowth.data.months.length - 1].value)
      : undefined);
  const lotGrowthValue = formatCents(lotGrowthRaw);

  const commissionsValue = formatCents(monthlyComm.data?.totals?.total);

  const activeLoansValue = formatCount(portfolio.data?.activeLoans ?? portfolio.data?.totalLoans);

  return (
    <div className="space-y-6">
      <div className="bg-slate-800/30 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
        <h2 className="text-xl font-bold text-white mb-1">Live Subscriber Dashboard Stats</h2>
        <p className="text-sm text-slate-400">
          Calls the same client-functions callables the subscriber's own dashboard hits.
          Founder role bypasses tenant gates server-side; every read is logged to
          founder cross-tenant audit.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Activity}
          label="Rolling 3-Run LOT"
          value={rolling3Value}
          loading={rolling3.loading}
          error={rolling3.error}
          onRefresh={() => void rolling3.refetch()}
          lastFetchedAt={rolling3.lastFetchedAt}
          iconColorClass="text-blue-400"
        />
        <StatCard
          icon={TrendingUp}
          label="Line of Trail (12mo)"
          value={lotGrowthValue}
          loading={lotGrowth.loading}
          error={lotGrowth.error}
          onRefresh={() => void lotGrowth.refetch()}
          lastFetchedAt={lotGrowth.lastFetchedAt}
          iconColorClass="text-green-400"
        />
        <StatCard
          icon={HandCoins}
          label="Commissions YTD"
          value={commissionsValue}
          loading={monthlyComm.loading}
          error={monthlyComm.error}
          onRefresh={() => void monthlyComm.refetch()}
          lastFetchedAt={monthlyComm.lastFetchedAt}
          iconColorClass="text-purple-400"
        />
        <StatCard
          icon={Vault}
          label="Active Loans"
          value={activeLoansValue}
          loading={portfolio.loading}
          error={portfolio.error}
          onRefresh={() => void portfolio.refetch()}
          lastFetchedAt={portfolio.lastFetchedAt}
          iconColorClass="text-amber-400"
        />
      </div>
    </div>
  );
};

// ─── E2E Tab ─────────────────────────────────────────────────────────────────

interface E2ETabProps {
  companyId: string;
}

interface E2ESurfaceResultLite {
  surface: string;
  passed: boolean;
  consoleErrors: string[];
  networkErrors: string[];
  durationMs: number;
  diagnostic?: string;
}

interface E2EJourneyReportLite {
  companyId: string;
  primaryUserUid: string;
  startedAt: string;
  finishedAt: string;
  passed: boolean;
  passA: E2ESurfaceResultLite[];
  passB: E2ESurfaceResultLite[];
  parity: { loanCountMatch: boolean; dashboardTotalsMatch: boolean; officeCountMatch: boolean };
  diagnostic: string;
  workflowRunUrl?: string;
  workflowRunId?: string;
}

const E2ETab: React.FC<E2ETabProps> = ({ companyId }) => {
  const [report, setReport] = useState<E2EJourneyReportLite | null>(null);
  const [reportAgeHours, setReportAgeHours] = useState<number | null>(null);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Initial fetch of latest report on mount + tab activation.
  const refreshReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      const callable = httpsCallable<
        { companyId: string },
        { report: E2EJourneyReportLite | null; ageHours: number | null }
      >(functions, 'getLatestE2EReport');
      const response = await callable({ companyId });
      setReport(response.data.report);
      setReportAgeHours(response.data.ageHours);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load latest E2E report';
      setReportError(msg);
    } finally {
      setReportLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refreshReport();
  }, [refreshReport]);

  return (
    <div className="space-y-6">
      {/* Latest report */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Latest E2E report</h3>
            {reportAgeHours !== null && (
              <p className="text-xs text-slate-500 mt-1">
                {reportAgeHours < 1
                  ? `${Math.round(reportAgeHours * 60)} minutes ago`
                  : `${reportAgeHours.toFixed(1)} hours ago`}
                <span className="text-amber-400 ml-2">
                  (historical report; the e2e journey was retired 2026-09-13, so
                  no newer run can be produced)
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => void refreshReport()}
            disabled={reportLoading}
            className="text-xs text-blue-400 hover:text-blue-300 transition-colors disabled:opacity-50"
          >
            {reportLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {reportError && (
          <p className="text-sm text-red-400">{reportError}</p>
        )}

        {!reportLoading && !reportError && !report && (
          <p className="text-sm text-slate-500">
            No E2E report stored for this company.
          </p>
        )}

        {report && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              {report.passed ? (
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/10 border border-green-500/30 text-green-400 rounded-full text-xs font-semibold">
                  PASSED
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/30 text-red-400 rounded-full text-xs font-semibold">
                  FAILED
                </span>
              )}
              <span className="text-xs text-slate-400">{report.diagnostic}</span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ReportPassPanel title="Pass A (founder via Console)" surfaces={report.passA} />
              <ReportPassPanel title="Pass B (real user via Firebase Auth)" surfaces={report.passB} />
            </div>

            <div className="bg-slate-900/50 rounded-lg p-3 text-xs">
              <strong className="text-slate-300">Parity check:</strong>
              <span className={`ml-2 ${report.parity.loanCountMatch ? 'text-green-400' : 'text-red-400'}`}>
                loan count {report.parity.loanCountMatch ? '✓' : '✗'}
              </span>
              <span className={`ml-3 ${report.parity.dashboardTotalsMatch ? 'text-green-400' : 'text-red-400'}`}>
                dashboard totals {report.parity.dashboardTotalsMatch ? '✓' : '✗'}
              </span>
              <span className={`ml-3 ${report.parity.officeCountMatch ? 'text-green-400' : 'text-red-400'}`}>
                office count {report.parity.officeCountMatch ? '✓' : '✗'}
              </span>
            </div>

            {report.workflowRunUrl && (
              <p className="text-xs text-slate-500">
                Workflow run:{' '}
                <a
                  href={report.workflowRunUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  {report.workflowRunUrl}
                </a>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ReportPassPanel: React.FC<{ title: string; surfaces: E2ESurfaceResultLite[] }> = ({
  title,
  surfaces,
}) => {
  const passed = surfaces.filter((s) => s.passed).length;
  return (
    <div className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-slate-200">{title}</h4>
        <span className="text-xs text-slate-500">
          {passed}/{surfaces.length} surfaces
        </span>
      </div>
      <ul className="space-y-1">
        {surfaces.slice(0, 12).map((s) => (
          <li key={s.surface} className="flex items-center gap-2 text-xs">
            <span className={s.passed ? 'text-green-400' : 'text-red-400'}>
              {s.passed ? '✓' : '✗'}
            </span>
            <code className="text-slate-300 font-mono truncate">{s.surface}</code>
          </li>
        ))}
        {surfaces.length > 12 && (
          <li className="text-xs text-slate-500">
            …and {surfaces.length - 12} more (see workflow artifact)
          </li>
        )}
      </ul>
    </div>
  );
};

export default SubscriberVerificationConsolePage;
